import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  BarChart3, Users, Calendar, Clock, Download,
  FileText, CheckCircle, XCircle, AlertTriangle,
  TrendingUp, TrendingDown, CalendarDays, Timer,
  UserCheck, UserX, Briefcase, PieChart
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: 1, label: 'يناير' },
  { value: 2, label: 'فبراير' },
  { value: 3, label: 'مارس' },
  { value: 4, label: 'أبريل' },
  { value: 5, label: 'مايو' },
  { value: 6, label: 'يونيو' },
  { value: 7, label: 'يوليو' },
  { value: 8, label: 'أغسطس' },
  { value: 9, label: 'سبتمبر' },
  { value: 10, label: 'أكتوبر' },
  { value: 11, label: 'نوفمبر' },
  { value: 12, label: 'ديسمبر' }
];

export default function HRReports() {
  const [employees, setEmployees] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [permissionRequests, setPermissionRequests] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    presentToday: 0,
    absentToday: 0,
    onLeave: 0,
    totalWorkHours: 0,
    avgWorkHours: 0,
    totalLateHours: 0,
    approvedLeaves: 0,
    pendingLeaves: 0,
    rejectedLeaves: 0
  });

  useEffect(() => {
    loadData();
  }, [selectedMonth, selectedYear, selectedEmployee]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [empRes, attRes, leaveRes, permRes] = await Promise.all([
        api.get('/hr/employees'),
        api.get(`/hr/attendance?month=${selectedMonth}&year=${selectedYear}`),
        api.get('/hr/leave-requests'),
        api.get('/hr/permission-requests')
      ]);

      setEmployees(empRes.data);
      
      let attendance = attRes.data;
      let leaves = leaveRes.data;
      let permissions = permRes.data;

      // Filter by employee if selected
      if (selectedEmployee !== 'all') {
        attendance = attendance.filter(a => a.employee_code === selectedEmployee);
        leaves = leaves.filter(l => l.employee_code === selectedEmployee);
        permissions = permissions.filter(p => p.employee_code === selectedEmployee);
      }

      setAttendanceData(attendance);
      setLeaveRequests(leaves);
      setPermissionRequests(permissions);

      // Calculate stats - استخدام التاريخ المحلي بدلاً من toISOString (لتجنب فرق التوقيت UTC)
      const todayDate = new Date();
      const today = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;
      const todayAttendance = attRes.data.filter(a => a.attendance_date === today);
      
      const totalWorkHours = attendance.reduce((sum, a) => sum + (a.work_hours || 0), 0);
      const totalLateMinutes = attendance.reduce((sum, a) => {
        if (a.check_in_time) {
          const checkIn = a.check_in_time.split(':');
          const checkInMinutes = parseInt(checkIn[0]) * 60 + parseInt(checkIn[1]);
          // Assuming work starts at 8:00 AM
          const startMinutes = 8 * 60;
          if (checkInMinutes > startMinutes) {
            return sum + (checkInMinutes - startMinutes);
          }
        }
        return sum;
      }, 0);

      setStats({
        totalEmployees: empRes.data.length,
        presentToday: todayAttendance.filter(a => a.check_in_time).length,
        absentToday: empRes.data.length - todayAttendance.filter(a => a.check_in_time).length,
        onLeave: leaves.filter(l => l.status === 'approved' && isDateInRange(today, l.start_date, l.end_date)).length,
        totalWorkHours: totalWorkHours.toFixed(1),
        avgWorkHours: attendance.length > 0 ? (totalWorkHours / attendance.length).toFixed(1) : 0,
        totalLateHours: (totalLateMinutes / 60).toFixed(1),
        approvedLeaves: leaves.filter(l => l.status === 'approved').length,
        pendingLeaves: leaves.filter(l => l.status === 'pending').length,
        rejectedLeaves: leaves.filter(l => l.status === 'rejected').length
      });

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const isDateInRange = (date, start, end) => {
    const d = new Date(date);
    const s = new Date(start);
    const e = new Date(end);
    return d >= s && d <= e;
  };

  const getEmployeeName = (empCode) => {
    const emp = employees.find(e => e.employee_code === empCode);
    return emp?.employee_name_ar || empCode;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500 hover:bg-green-600">موافق</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">معلق</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500 hover:bg-red-600">مرفوض</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const downloadAttendanceReport = async () => {
    try {
      toast.loading('جاري إنشاء التقرير...', { id: 'report' });
      const response = await fetch(
        `${BACKEND_URL}/api/hr/reports/attendance?month=${selectedMonth}&year=${selectedYear}${selectedEmployee !== 'all' ? `&employee_code=${selectedEmployee}` : ''}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to generate report');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `تقرير_الحضور_${selectedMonth}_${selectedYear}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss('report');
      toast.success('تم تحميل التقرير بنجاح');
    } catch (error) {
      toast.dismiss('report');
      toast.error('حدث خطأ في إنشاء التقرير');
    }
  };

  const downloadLeaveReport = async () => {
    try {
      toast.loading('جاري إنشاء التقرير...', { id: 'report' });
      const response = await fetch(
        `${BACKEND_URL}/api/hr/reports/leaves?year=${selectedYear}${selectedEmployee !== 'all' ? `&employee_code=${selectedEmployee}` : ''}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to generate report');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `تقرير_الإجازات_${selectedYear}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss('report');
      toast.success('تم تحميل التقرير بنجاح');
    } catch (error) {
      toast.dismiss('report');
      toast.error('حدث خطأ في إنشاء التقرير');
    }
  };

  // Group attendance by employee for summary
  const attendanceSummary = employees.map(emp => {
    const empAttendance = attendanceData.filter(a => a.employee_code === emp.employee_code);
    const presentDays = empAttendance.filter(a => a.check_in_time).length;
    const totalHours = empAttendance.reduce((sum, a) => sum + (a.work_hours || 0), 0);
    const lateDays = empAttendance.filter(a => {
      if (a.check_in_time) {
        const checkIn = a.check_in_time.split(':');
        return parseInt(checkIn[0]) >= 8 && parseInt(checkIn[1]) > 15;
      }
      return false;
    }).length;

    return {
      employee_code: emp.employee_code,
      employee_name: emp.employee_name_ar,
      department: emp.department || '-',
      present_days: presentDays,
      absent_days: getDaysInMonth(selectedMonth, selectedYear) - presentDays,
      total_hours: totalHours.toFixed(1),
      late_days: lateDays
    };
  });

  function getDaysInMonth(month, year) {
    return new Date(year, month, 0).getDate();
  }

  return (
    <div className="space-y-6" data-testid="hr-reports-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-emerald-600" />
            تقارير الموارد البشرية
          </h1>
          <p className="text-muted-foreground">تقارير تفصيلية للحضور والإجازات وأداء الموظفين</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label>الشهر:</Label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="h-10 px-3 border rounded-lg bg-white"
              data-testid="month-filter"
            >
              {MONTHS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Label>السنة:</Label>
            <Input
              type="number"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-24"
              min={2020}
              max={2030}
            />
          </div>
          <div className="flex items-center gap-2">
            <Label>الموظف:</Label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="h-10 px-3 border rounded-lg bg-white min-w-[200px]"
              data-testid="employee-filter"
            >
              <option value="all">جميع الموظفين</option>
              {employees.map(emp => (
                <option key={emp.employee_code} value={emp.employee_code}>
                  {emp.employee_name_ar} ({emp.employee_code})
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">إجمالي الموظفين</p>
              <p className="text-2xl font-bold">{stats.totalEmployees}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">حاضرين اليوم</p>
              <p className="text-2xl font-bold text-green-600">{stats.presentToday}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
              <UserX className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">غائبين اليوم</p>
              <p className="text-2xl font-bold text-red-600">{stats.absentToday}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Clock className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">إجمالي ساعات العمل</p>
              <p className="text-2xl font-bold">{stats.totalWorkHours} <span className="text-sm">ساعة</span></p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">ساعات التأخير</p>
              <p className="text-2xl font-bold text-orange-600">{stats.totalLateHours} <span className="text-sm">ساعة</span></p>
            </div>
          </div>
        </Card>
      </div>

      {/* Leave Statistics */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 border-l-4 border-green-500">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-sm text-muted-foreground">إجازات موافق عليها</p>
              <p className="text-2xl font-bold text-green-600">{stats.approvedLeaves}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-yellow-500">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-sm text-muted-foreground">إجازات معلقة</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pendingLeaves}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-red-500">
          <div className="flex items-center gap-3">
            <XCircle className="w-8 h-8 text-red-500" />
            <div>
              <p className="text-sm text-muted-foreground">إجازات مرفوضة</p>
              <p className="text-2xl font-bold text-red-600">{stats.rejectedLeaves}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs for different reports */}
      <Tabs defaultValue="attendance" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="attendance" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            تقرير الحضور
          </TabsTrigger>
          <TabsTrigger value="leaves" className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4" />
            تقرير الإجازات
          </TabsTrigger>
          <TabsTrigger value="performance" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            أداء الموظفين
          </TabsTrigger>
        </TabsList>

        {/* Attendance Report Tab */}
        <TabsContent value="attendance">
          <Card className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg">ملخص الحضور الشهري - {MONTHS[selectedMonth - 1]?.label} {selectedYear}</h3>
              <Button variant="outline" onClick={downloadAttendanceReport}>
                <Download className="w-4 h-4 me-2" />
                تحميل PDF
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-3 text-start">#</th>
                    <th className="p-3 text-start">الموظف</th>
                    <th className="p-3 text-center">القسم</th>
                    <th className="p-3 text-center">أيام الحضور</th>
                    <th className="p-3 text-center">أيام الغياب</th>
                    <th className="p-3 text-center">أيام التأخير</th>
                    <th className="p-3 text-center">إجمالي الساعات</th>
                    <th className="p-3 text-center">نسبة الحضور</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceSummary.map((row, idx) => {
                    const totalDays = getDaysInMonth(selectedMonth, selectedYear);
                    const attendanceRate = ((row.present_days / totalDays) * 100).toFixed(0);
                    return (
                      <tr key={row.employee_code} className="border-b hover:bg-muted/30">
                        <td className="p-3">{idx + 1}</td>
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{row.employee_name}</p>
                            <p className="text-xs text-muted-foreground">{row.employee_code}</p>
                          </div>
                        </td>
                        <td className="p-3 text-center">{row.department}</td>
                        <td className="p-3 text-center font-medium text-green-600">{row.present_days}</td>
                        <td className="p-3 text-center font-medium text-red-600">{row.absent_days}</td>
                        <td className="p-3 text-center font-medium text-orange-600">{row.late_days}</td>
                        <td className="p-3 text-center font-mono">{row.total_hours}</td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${
                                  attendanceRate >= 80 ? 'bg-green-500' : 
                                  attendanceRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${attendanceRate}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium">{attendanceRate}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Leaves Report Tab */}
        <TabsContent value="leaves">
          <Card className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg">تقرير الإجازات - {selectedYear}</h3>
              <Button variant="outline" onClick={downloadLeaveReport}>
                <Download className="w-4 h-4 me-2" />
                تحميل PDF
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-3 text-start">#</th>
                    <th className="p-3 text-start">الموظف</th>
                    <th className="p-3 text-center">نوع الإجازة</th>
                    <th className="p-3 text-center">من</th>
                    <th className="p-3 text-center">إلى</th>
                    <th className="p-3 text-center">عدد الأيام</th>
                    <th className="p-3 text-center">الحالة</th>
                    <th className="p-3 text-center">تاريخ الطلب</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveRequests.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="p-8 text-center text-muted-foreground">
                        لا توجد طلبات إجازة
                      </td>
                    </tr>
                  ) : (
                    leaveRequests.map((leave, idx) => (
                      <tr key={leave.request_id} className="border-b hover:bg-muted/30">
                        <td className="p-3">{idx + 1}</td>
                        <td className="p-3">{getEmployeeName(leave.employee_code)}</td>
                        <td className="p-3 text-center">{leave.leave_type_code}</td>
                        <td className="p-3 text-center font-mono">{leave.start_date}</td>
                        <td className="p-3 text-center font-mono">{leave.end_date}</td>
                        <td className="p-3 text-center font-medium">{leave.days_count}</td>
                        <td className="p-3 text-center">{getStatusBadge(leave.status)}</td>
                        <td className="p-3 text-center font-mono text-xs">
                          {new Date(leave.created_at).toLocaleDateString('ar-SA')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Performance Report Tab */}
        <TabsContent value="performance">
          <Card className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg">تقرير أداء الموظفين - {MONTHS[selectedMonth - 1]?.label} {selectedYear}</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {attendanceSummary.map((emp) => {
                const totalDays = getDaysInMonth(selectedMonth, selectedYear);
                const attendanceRate = ((emp.present_days / totalDays) * 100).toFixed(0);
                const avgHoursPerDay = emp.present_days > 0 ? (parseFloat(emp.total_hours) / emp.present_days).toFixed(1) : 0;
                
                let performanceLevel = 'ممتاز';
                let performanceColor = 'text-green-600';
                let performanceBg = 'bg-green-100';
                
                if (attendanceRate < 60) {
                  performanceLevel = 'ضعيف';
                  performanceColor = 'text-red-600';
                  performanceBg = 'bg-red-100';
                } else if (attendanceRate < 80) {
                  performanceLevel = 'متوسط';
                  performanceColor = 'text-yellow-600';
                  performanceBg = 'bg-yellow-100';
                } else if (attendanceRate < 90) {
                  performanceLevel = 'جيد';
                  performanceColor = 'text-blue-600';
                  performanceBg = 'bg-blue-100';
                }

                return (
                  <Card key={emp.employee_code} className="p-4 border">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="font-semibold">{emp.employee_name}</h4>
                        <p className="text-sm text-muted-foreground">{emp.employee_code} | {emp.department}</p>
                      </div>
                      <Badge className={`${performanceBg} ${performanceColor}`}>
                        {performanceLevel}
                      </Badge>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">نسبة الحضور</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                attendanceRate >= 80 ? 'bg-green-500' : 
                                attendanceRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${attendanceRate}%` }}
                            />
                          </div>
                          <span className="font-medium">{attendanceRate}%</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="p-2 bg-muted rounded">
                          <p className="text-lg font-bold text-green-600">{emp.present_days}</p>
                          <p className="text-xs text-muted-foreground">حضور</p>
                        </div>
                        <div className="p-2 bg-muted rounded">
                          <p className="text-lg font-bold text-red-600">{emp.absent_days}</p>
                          <p className="text-xs text-muted-foreground">غياب</p>
                        </div>
                        <div className="p-2 bg-muted rounded">
                          <p className="text-lg font-bold text-orange-600">{emp.late_days}</p>
                          <p className="text-xs text-muted-foreground">تأخير</p>
                        </div>
                      </div>

                      <div className="flex justify-between text-sm pt-2 border-t">
                        <span className="text-muted-foreground">إجمالي الساعات:</span>
                        <span className="font-medium">{emp.total_hours} ساعة</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">متوسط الساعات/يوم:</span>
                        <span className="font-medium">{avgHoursPerDay} ساعة</span>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
